import { useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, CalendarRange } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const PRIMARY = "#018D4C";
const PREV = "#D4D4D8";
const TOOLTIP_STYLE = { background: "#0A0A0A", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 };

const fmtMonth = (iso) =>
  iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" }) : "";

// `goodWhen` says which direction is an improvement: more articles is good,
// more cannibalization is bad.
function Delta({ change, pct, goodWhen = "up" }) {
  if (!change) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-zinc-400">
        <Minus size={12} strokeWidth={2.5} /> no change
      </span>
    );
  }
  const up = change > 0;
  const good = goodWhen === "up" ? up : !up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${good ? "text-green-600" : "text-red-500"}`}>
      <Icon size={13} strokeWidth={2.5} />
      {up ? "+" : ""}{change}{pct != null ? ` (${up ? "+" : ""}${pct}%)` : ""}
    </span>
  );
}

function CompareCard({ label, now, prev, prevLabel, change, pct, goodWhen, sub, testid, children }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{label}</p>
        {children}
      </div>
      <p className="text-3xl font-bold text-zinc-900 tabular-nums">{now}</p>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <Delta change={change} pct={pct} goodWhen={goodWhen} />
        <span className="text-xs text-zinc-400">vs {prev} {prevLabel}</span>
      </div>
      {sub && <p className="text-[10px] text-zinc-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function OverviewTrends({ trends }) {
  const [period, setPeriod] = useState("month");
  if (!trends) return null;

  const { publishing: pace, cannibalization: cannibal, published_total: totals, topics, source } = trends;
  const isWeek = period === "week";
  const paceNow = isWeek ? pace.this_week : pace.this_month;
  const pacePrev = isWeek ? pace.last_week : pace.last_month;
  const prevMonth = fmtMonth(trends.last_month);

  const topicData = topics.map((t) => ({
    name: t.label.length > 16 ? `${t.label.slice(0, 16)}…` : t.label,
    full: t.label,
    [prevMonth]: t.last_month,
    Now: t.this_month,
    added: t.added_this_month,
    isNew: t.is_new,
  }));
  const grew = topics.filter((t) => t.this_month > t.last_month).length;

  return (
    <div data-testid="overview-trends" className="mt-6">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarRange size={14} className="text-zinc-500" strokeWidth={2.5} />
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
            This month vs {prevMonth}
          </p>
        </div>
        <span
          data-testid="trends-source"
          title={
            source === "snapshot"
              ? "Last month's numbers were saved at the end of that month."
              : "No saved numbers for last month yet. Estimated by removing articles published this month; deleted articles and edits aren't reflected."
          }
          className={`text-[10px] px-2 py-0.5 rounded-full border ${
            source === "snapshot" ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          {source === "snapshot" ? "Saved snapshot" : "Estimated from publish dates"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CompareCard
          testid="trend-publishing"
          label={isWeek ? "Published this week" : "Published this month"}
          now={paceNow}
          prev={pacePrev}
          prevLabel={isWeek ? "last week" : "last month"}
          change={paceNow - pacePrev}
          pct={isWeek ? pace.week_change_pct : pace.month_change_pct}
          goodWhen="up"
          sub={pace.undated > 0 ? `${pace.undated} published article${pace.undated === 1 ? "" : "s"} have no publish date` : undefined}
        >
          <div className="flex rounded-md border border-zinc-200 overflow-hidden text-[10px] font-bold">
            {["week", "month"].map((p) => (
              <button
                key={p}
                data-testid={`trend-period-${p}`}
                onClick={() => setPeriod(p)}
                className={`px-2 py-0.5 uppercase tracking-wider ${period === p ? "text-white" : "text-zinc-500 hover:bg-zinc-50"}`}
                style={period === p ? { backgroundColor: PRIMARY } : undefined}
              >
                {p}
              </button>
            ))}
          </div>
        </CompareCard>
        <CompareCard
          testid="trend-cannibalization"
          label="Cannibalization pairs"
          now={cannibal.now}
          prev={cannibal.last_month}
          prevLabel={`end of ${prevMonth}`}
          change={cannibal.change}
          pct={cannibal.last_month ? Math.round((cannibal.change / cannibal.last_month) * 1000) / 10 : null}
          goodWhen="down"
        />
        <CompareCard
          testid="trend-library"
          label="Published library"
          now={totals.now}
          prev={totals.last_month}
          prevLabel={`end of ${prevMonth}`}
          change={totals.change}
          goodWhen="up"
          sub={topics.length ? `${grew} of ${trends.topics_total} topics grew${trends.topics_dropped ? ` · ${trends.topics_dropped} gone` : ""}` : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">
            Topic distribution — now vs end of {prevMonth}
          </p>
          {topicData.length === 0 ? (
            <p className="text-xs text-zinc-400 py-12 text-center">No topics yet. Sync to build clusters.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={topicData} barGap={2} barSize={10}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  cursor={{ fill: "#F4F4F5" }}
                  labelFormatter={(_, p) => {
                    const d = p?.[0]?.payload;
                    if (!d) return "";
                    return `${d.full}${d.isNew ? " (new topic)" : ""}${d.added ? ` · +${d.added} published this month` : ""}`;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                <Bar dataKey={prevMonth} fill={PREV} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Now" fill={PRIMARY} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">Published per month</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pace.series} barSize={18}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#F4F4F5" }} />
              <Bar dataKey="published" name="Published" fill={PRIMARY} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
